import { useMemo, useSyncExternalStore } from 'react';

function readRoleFromCookie(): string {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(/(?:^|;\s*)role=([^;]*)/);
  if (!match?.[1]) return '';
  try {
    return decodeURIComponent(match[1].replace(/\+/g, ' ')).trim();
  } catch {
    return match[1].trim();
  }
}

const getStoredRole = () => localStorage.getItem('role') || readRoleFromCookie() || '';

/** Same-tab localStorage updates do not fire `storage`; poll + cross-tab storage. */
function subscribeStoredRole(onChange: () => void) {
  const onStorage = (e: StorageEvent) => {
    if (e.key === 'role' || e.key === null) onChange();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener('focus', onChange);
  const intervalId = window.setInterval(onChange, 800);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('focus', onChange);
    window.clearInterval(intervalId);
  };
}

/** API may return "developer", "Developer", "project_developer", etc. */
export function isDeveloperRoleString(role: string): boolean {
  const r = role.trim().toLowerCase();
  if (!r) return false;
  if (r === 'developer' || r === 'dev') return true;
  if (r.includes('developer')) return true;
  return false;
}

export interface RolePermissions {
  rawRole: string;
  role: string;
  isDeveloper: boolean;
  isReviewer: boolean;
  isAdmin: boolean;
  canManageWhitelist: boolean;
  canSelectWhitelistRules: boolean;
  canSaveWhitelist: boolean;
  canSubmitForReview: boolean;
  canReviewPending: boolean;
}

export const useRolePermissions = (): RolePermissions => {
  const rawRole = useSyncExternalStore(subscribeStoredRole, getStoredRole, getStoredRole);

  const normalizedRole = rawRole.trim().toLowerCase();

  return useMemo(() => {
    const isDeveloper = isDeveloperRoleString(rawRole);
    const isReviewer = normalizedRole === 'reviewer';
    const isAdmin = normalizedRole === 'admin';

    return {
      rawRole,
      role: normalizedRole,
      isDeveloper,
      isReviewer,
      isAdmin,
      canManageWhitelist: !isDeveloper,
      canSelectWhitelistRules: !isDeveloper,
      canSaveWhitelist: !isDeveloper,
      canSubmitForReview: isDeveloper,
      canReviewPending: isReviewer || isAdmin
    };
  }, [normalizedRole, rawRole]);
};
